import React, { useState, useEffect, useCallback } from 'react';
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import { getErrorMessage } from '../../utils/errorHandler';

interface ManagementIndicatorsProps {
    onBack: () => void;
    tenantName: string;
    selectedYear: number;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

interface Indicator {
    id: string;
    name: string;
    value: number;
    unit: string;
    description: string;
    target: number;
    better: 'higher' | 'lower';
}

interface RawData {
    current_assets: number;
    quick_assets: number;
    current_liabilities: number;
    net_assets: number;
    total_liabilities: number;
    total_revenue: number;
    personnel_expenses: number;
    service_balance: number;
}

const ManagementIndicators: React.FC<ManagementIndicatorsProps> = ({
    onBack,
    tenantName,
    selectedYear,
    apiFetch
}) => {
    const [loading, setLoading] = useState(false);
    const [errorStr, setErrorStr] = useState<string | null>(null);
    const [indicators, setIndicators] = useState<Indicator[]>([]);
    const [rawData, setRawData] = useState<RawData | null>(null);

    const fetchIndicators = useCallback(async () => {
        setLoading(true);
        setErrorStr(null);
        try {
            const res = await apiFetch(`/api/analysis/management-indicators?fiscal_year=${selectedYear}`);
            if (!res.ok) {
                const msg = await getErrorMessage(res, '経営指標の取得に失敗しました');
                throw new Error(msg);
            }
            const data = await res.json();
            setIndicators(data.indicators);
            setRawData(data.raw_data);
        } catch (e: any) {
            setErrorStr(e.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, selectedYear]);

    useEffect(() => {
        fetchIndicators();
    }, [fetchIndicators]);

    // レーダーチャート用のデータ変形
    // 各指標の達成度（評価スコア）を 0〜100 の相対値で強引に丸める（描画用）
    const radarData = indicators.map(ind => {
        let score = 0;
        if (ind.better === 'higher') {
            score = ind.target > 0 ? (ind.value / ind.target) * 100 : 0;
        } else {
            score = ind.value > 0 ? (ind.target / ind.value) * 100 : 0;
        }
        // 上限100、下限0
        score = Math.min(Math.max(score, 0), 100);

        return {
            subject: ind.name,
            A: score,
            fullMark: 100,
        };
    });

    // 数値フォーマットヘルパー
    const formatAmt = (val: number) => new Intl.NumberFormat('ja-JP').format(val);

    return (
        <div style={{ padding: '20px', backgroundColor: '#f9f9f9', minHeight: 'calc(100vh - 120px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #ccc', paddingBottom: '8px' }}>
                <h2 style={{ fontSize: '18px', margin: 0 }}>
                    経営指標分析表 (R-26) - {selectedYear}年度
                </h2>
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>対象: {tenantName}</div>
            </div>

            {errorStr && (
                <div style={{ color: 'red', border: '1px solid red', padding: '8px', marginBottom: '12px', backgroundColor: '#fee' }}>
                    {errorStr}
                </div>
            )}

            {loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>
            ) : indicators.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                    {/* 左側：指標リストと説明 */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {indicators.map((ind) => {
                            const isGood = ind.better === 'higher' ? ind.value >= ind.target : ind.value <= ind.target;
                            const color = isGood ? '#2e7d32' : '#c62828';

                            return (
                                <div key={ind.id} style={{ backgroundColor: '#fff', border: '1px solid #ddd', padding: '16px', borderRadius: '4px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                                        <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>{ind.name}</h3>
                                        <div style={{ fontSize: '20px', fontWeight: 'bold', color }}>
                                            {ind.value} <span style={{ fontSize: '14px' }}>{ind.unit}</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
                                        {ind.description}
                                    </div>
                                    <div style={{ fontSize: '12px', backgroundColor: '#f1f1f1', padding: '6px', borderRadius: '2px', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>目標/目安: {ind.target}{ind.unit} {ind.better === 'higher' ? '以上' : '以下'}</span>
                                        <span style={{ fontWeight: 'bold', color }}>
                                            {isGood ? '達成 (良好)' : '要改善'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* 右側：グラフと元データ */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', height: '400px' }}>
                            <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', textAlign: 'center' }}>経営バランス（レーダーチャート）</h3>
                            <ResponsiveContainer width="100%" height="85%">
                                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                                    {/* @ts-ignore */}
                                    <PolarGrid />
                                    {/* @ts-ignore */}
                                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                                    {/* @ts-ignore */}
                                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                                    {/* @ts-ignore */}
                                    <Radar name="達成度" dataKey="A" stroke="#0066cc" fill="#0066cc" fillOpacity={0.4} />
                                    {/* @ts-ignore */}
                                    <Tooltip />
                                </RadarChart>
                            </ResponsiveContainer>
                            <div style={{ fontSize: '11px', textAlign: 'center', color: '#666' }}>※各指標の目安を100とした場合の相対的な達成度です。</div>
                        </div>

                        {rawData && (
                            <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', padding: '16px', borderRadius: '4px' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '14px' }}>算定基礎データ</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>流動資産:</span> <span>{formatAmt(rawData.current_assets)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>流動負債:</span> <span>{formatAmt(rawData.current_liabilities)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>当座資産:</span> <span>{formatAmt(rawData.quick_assets)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>負債合計:</span> <span>{formatAmt(rawData.total_liabilities)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>純資産:</span> <span>{formatAmt(rawData.net_assets)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>事業活動収入計:</span> <span>{formatAmt(rawData.total_revenue)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>人件費:</span> <span>{formatAmt(rawData.personnel_expenses)} 円</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #ccc' }}>
                                        <span>事業活動収支差額:</span> <span>{formatAmt(rawData.service_balance)} 円</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>データがありません。</div>
            )}

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <button
                    onClick={onBack}
                    style={{ padding: '8px 24px', fontSize: '14px', backgroundColor: '#e0e0e0', border: '1px outset #ccc', cursor: 'pointer' }}
                >
                    メニューへ戻る
                </button>
            </div>
        </div>
    );
};

export default ManagementIndicators;
